// Lib
import { clamp } from 'lodash';

// Constants
import { ZOOM_PROPS } from '../zoomConstants';

const asPercentage = (ratio: number): number => ratio * 100;
const asRatio = (percentage: number): number => percentage / 100;

// NOTE: These rely on the formula:
//    scale = focalLength / (focalLength - step)
const ZOOM_STEP_COUNT = 4;
// We need to reach the max scale at the zoom step count
const FOCAL_LENGTH_ZOOM_IN = (ZOOM_PROPS.MAX_SCALE * ZOOM_STEP_COUNT) / (ZOOM_PROPS.MAX_SCALE - 1);

/**
 * Determines the current zoom index based on the current scale when in the zoomed in mode (>100%).
 */
const getCurrentStepIndex = (currentScale: number, focalLength: number): number => {
    const index = focalLength - focalLength / currentScale;
    // If the index is really close to the higher number, use the higher number
    if (index % 1 > 0.99 || (index % 1 < 0 && index % 1 > -0.001)) return Math.ceil(index);
    return Math.floor(index);
};

/**
 * Based on a step index, determines the scale when in the zoomed in mode (>100%).
 */
const getScaleFromIndex = (index: number, focalLength: number): number => focalLength / (focalLength - index);

/**
 * Based on the current scale, and whether we're stepping forwards (+1) or backwards (-1)
 * this will determine the next scale to use
 */
const getNextScale = (currentScale: number, focalLength: number, step = 1, min: number, max: number): number => {
    const currentIndex = getCurrentStepIndex(currentScale, focalLength);
    const nextIndex = currentIndex + step;
    const nextScale = getScaleFromIndex(nextIndex, focalLength);
    return clamp(nextScale, min, max);
};

/**
 * Determines the focal length to use to ensure there's 4 steps between 100% and the minimum scale.
 */
const getFocalLengthZoomedOut = (minScale: number): number => -(minScale * ZOOM_STEP_COUNT) / (minScale - 1);

/**
 * Decrements the scale to the next step down.
 */
export const decrementZoomScale = (currentScale: number, minScale: number): number => {
    const stepChange = -1;

    if (currentScale <= 1) {
        const focalLength = getFocalLengthZoomedOut(minScale);
        return getNextScale(currentScale, focalLength, stepChange, minScale, 1);
    }

    return getNextScale(currentScale, FOCAL_LENGTH_ZOOM_IN, stepChange, 1, ZOOM_PROPS.MAX_SCALE);
};

/**
 * Calculates the next scale to step down to when clicking the "-" button, or using the decrement zoom shortcut.
 */
export const decrementZoomScalePercentage =
    (minScalePercentage: number) =>
    (currentScalePercentage: number): number => {
        const minScale = asRatio(minScalePercentage);
        const currentScale = asRatio(currentScalePercentage);
        const nextScale = decrementZoomScale(currentScale, minScale);
        return asPercentage(nextScale);
    };

export const incrementZoomScale = (currentScale: number, minScale: number): number => {
    const stepChange = 1;

    if (currentScale < 1) {
        const focalLength = getFocalLengthZoomedOut(minScale);
        return getNextScale(currentScale, focalLength, stepChange, minScale, 1);
    }

    return getNextScale(currentScale, FOCAL_LENGTH_ZOOM_IN, stepChange, 1, ZOOM_PROPS.MAX_SCALE);
};

/**
 * Calculates the next scale to step up to when clicking the "+" button, or using the increment zoom shortcut.
 */
export const incrementZoomScalePercentage =
    (minScalePercentage: number) =>
    (currentScalePercentage: number): number => {
        const minScale = asRatio(minScalePercentage);
        const currentScale = asRatio(currentScalePercentage);
        const nextScale = incrementZoomScale(currentScale, minScale);
        return asPercentage(nextScale);
    };
